import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
	StraightenerProduct,
	StraightenerRecipeValueRow,
	StraightenerRecipeVersion,
} from "../models/StraightenerRecipe";
import { STR_TREND_KEYS } from "../../straightener-trend-display/hooks/useStraightenerTrend";
import {
	getCurrentCrew,
	getCurrentLineValues,
	getStraightenerProducts,
	getStraightenerRecipeValues,
	getStraightenerRecipes,
	removeStraightenerRecipe,
	saveStraightenerRecipe,
	setStraightenerRecipeActive,
	setStraightenerRecipeCurrent,
	setStraightenerRecipePlcCurrent,
	updateStraightenerRecipe,
} from "../repositories/StraightenerRecipesRepository";

// Read procedures the API reports as stale after a straightener write (see the
// QueryCacheInvalidations block in PostDeployment.sql). Every query under
// STR_RECIPE_KEYS.all declares them, so a recipe change made on the other
// straightener screen, or in another operator's browser, refreshes it here.
// The Recipes screen does not poll, so it relies on this.
export const STR_RECIPE_REALTIME = [
	"MILL.GetStraightenerRecipes",
	"MILL.GetStraightenerRecipeValues",
];

export const STR_RECIPE_KEYS = {
	all: ["straightenerRecipes"] as const,
	products: ["straightenerRecipes", "products"] as const,
	list: ["straightenerRecipes", "list"] as const,
	values: (recipeId: number | null) =>
		["straightenerRecipes", "values", recipeId] as const,
	currentCrew: ["straightenerRecipes", "currentCrew"] as const,
	currentLineValues: ["straightenerRecipes", "currentLineValues"] as const,
};

export const useCurrentCrew = () =>
	useQuery<string | null, Error>({
		queryKey: STR_RECIPE_KEYS.currentCrew,
		queryFn: getCurrentCrew,
		staleTime: 60 * 1000,
		meta: { realtime: STR_RECIPE_REALTIME },
	});

// The current line values are only ever read imperatively (the "start from
// current line values" prefill), through `queryClient.fetchQuery`: the prefill
// must use the resolved values, not whatever the cache holds at that instant —
// on the editor's first open the request is still in flight.
export const CURRENT_LINE_VALUES_QUERY = {
	queryKey: STR_RECIPE_KEYS.currentLineValues,
	queryFn: getCurrentLineValues,
	staleTime: 30 * 1000,
	meta: { realtime: STR_RECIPE_REALTIME },
};

export const useStraightenerProducts = () =>
	useQuery<StraightenerProduct[], Error>({
		queryKey: STR_RECIPE_KEYS.products,
		queryFn: getStraightenerProducts,
		staleTime: 5 * 60 * 1000,
		meta: { realtime: STR_RECIPE_REALTIME },
	});

export const useStraightenerRecipes = () =>
	useQuery<StraightenerRecipeVersion[], Error>({
		queryKey: STR_RECIPE_KEYS.list,
		queryFn: () => getStraightenerRecipes(null),
		meta: { realtime: STR_RECIPE_REALTIME },
	});

export const useStraightenerRecipeValues = (recipeId: number | null) =>
	useQuery<StraightenerRecipeValueRow[], Error>({
		queryKey: STR_RECIPE_KEYS.values(recipeId),
		queryFn: () => getStraightenerRecipeValues(recipeId as number),
		enabled: !!recipeId,
		meta: { realtime: STR_RECIPE_REALTIME },
	});

interface MutationOptions<T> {
	onSuccess?: (data: T) => void;
	onError?: (error: Error) => void;
}

export const useSaveStraightenerRecipe = ({
	onSuccess,
	onError,
}: MutationOptions<number> = {}) => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (args: {
			productSizeId: number;
			name: string | null;
			crew: string | null;
			comments: string | null;
			values: StraightenerRecipeValueRow[];
			setAsCurrent?: boolean;
		}) =>
			saveStraightenerRecipe(
				args.productSizeId,
				args.name,
				args.crew,
				args.comments,
				args.values,
				args.setAsCurrent ?? false,
			),
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: STR_RECIPE_KEYS.all });
			onSuccess?.(data);
		},
		onError: (error: Error) => onError?.(error),
	});
	return { saveRecipe: mutation.mutate, isSaving: mutation.isPending };
};

export const useUpdateStraightenerRecipe = ({
	onSuccess,
	onError,
}: MutationOptions<number> = {}) => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (args: {
			recipeId: number;
			name: string | null;
			crew: string | null;
			comments: string | null;
			values: StraightenerRecipeValueRow[];
		}) =>
			updateStraightenerRecipe(
				args.recipeId,
				args.name,
				args.crew,
				args.comments,
				args.values,
			),
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: STR_RECIPE_KEYS.all });
			onSuccess?.(data);
		},
		onError: (error: Error) => onError?.(error),
	});
	return { updateRecipe: mutation.mutate, isUpdating: mutation.isPending };
};

export const useSetStraightenerRecipeCurrent = ({
	onSuccess,
	onError,
}: MutationOptions<void> = {}) => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (recipeId: number) => setStraightenerRecipeCurrent(recipeId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: STR_RECIPE_KEYS.all });
			queryClient.invalidateQueries({ queryKey: STR_TREND_KEYS.all });
			onSuccess?.();
		},
		onError: (error: Error) => onError?.(error),
	});
	return { setCurrent: mutation.mutate, isSettingCurrent: mutation.isPending };
};

export const useSetStraightenerRecipePlcCurrent = ({
	onSuccess,
	onError,
}: MutationOptions<void> = {}) => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (recipeId: number) => setStraightenerRecipePlcCurrent(recipeId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: STR_RECIPE_KEYS.all });
			queryClient.invalidateQueries({ queryKey: STR_TREND_KEYS.all });
			onSuccess?.();
		},
		onError: (error: Error) => onError?.(error),
	});
	return { setPlcCurrent: mutation.mutate, isSettingPlc: mutation.isPending };
};

export const useSetStraightenerRecipeActive = ({
	onSuccess,
	onError,
}: MutationOptions<void> = {}) => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (args: {
			recipeId: number;
			active: boolean;
			newCurrentRecipeId?: number | null;
		}) =>
			setStraightenerRecipeActive(
				args.recipeId,
				args.active,
				args.newCurrentRecipeId ?? null,
			),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: STR_RECIPE_KEYS.all });
			queryClient.invalidateQueries({ queryKey: STR_TREND_KEYS.all });
			onSuccess?.();
		},
		onError: (error: Error) => onError?.(error),
	});
	return { setActive: mutation.mutate, isSettingActive: mutation.isPending };
};

export const useRemoveStraightenerRecipe = ({
	onSuccess,
	onError,
}: MutationOptions<void> = {}) => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (id: number) => removeStraightenerRecipe(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: STR_RECIPE_KEYS.all });
			onSuccess?.();
		},
		onError: (error: Error) => onError?.(error),
	});
	return { removeRecipe: mutation.mutate, isRemoving: mutation.isPending };
};
